import { useEffect, useState } from 'react';
import MuxPlayer from '@mux/mux-player-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { isHlsUrl } from '../lib/attachments';
import { safeMediaSrc } from '@/lib/mediaUrl';

function clamp(n: number, min: number, max: number): number {
  return Math.min(Math.max(n, min), max);
}

export interface MediaViewerState {
  url: string;
  isVideo: boolean;
  playbackId: string | null;
}

/**
 * Full-screen media viewer (doc 09 §4.9). Image -> wheel zoom (scale clamped 1..8); video -> Mux HLS
 * player when a playbackId / .m3u8 is present, otherwise a native <video>. No wasm hole-punch — the
 * native element composes correctly inside the dialog. Signed URLs render directly.
 */
export function MediaViewerDialog({
  media,
  onClose,
}: {
  media: MediaViewerState | null;
  onClose: () => void;
}) {
  const [scale, setScale] = useState(1);

  useEffect(() => {
    setScale(1);
  }, [media]);

  if (!media) return null;
  const { url, isVideo, playbackId } = media;
  const hls = isHlsUrl(url);
  const imageSrc = safeMediaSrc(url);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-[90vw] border-0 bg-black p-0 sm:max-w-3xl">
        <DialogTitle className="sr-only">Media viewer</DialogTitle>
        <div className="flex h-[80vh] items-center justify-center overflow-hidden">
          {isVideo ? (
            playbackId ? (
              <MuxPlayer
                streamType="on-demand"
                playbackId={playbackId}
                className="max-h-full w-full"
                accentColor="#0194F5"
              />
            ) : hls && url ? (
              <MuxPlayer streamType="on-demand" src={url} className="max-h-full w-full" accentColor="#0194F5" />
            ) : url ? (
              <video src={url} controls playsInline className="max-h-full w-full object-contain" />
            ) : (
              <p className="text-sm text-white/70">Video URL not available</p>
            )
          ) : imageSrc ? (
            <img
              src={imageSrc}
              alt=""
              style={{ transform: `scale(${scale})` }}
              onWheel={(e) => setScale((s) => clamp(s * (e.deltaY < 0 ? 1.1 : 0.9), 1, 8))}
              className="max-h-full w-full object-contain transition-transform"
            />
          ) : (
            <p className="text-sm text-white/70">Media not available</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
